import { DotNetType, createTypedField } from 'tessa/platform';
import { showLoadingOverlay, showNotEmpty } from 'tessa/ui';
import { getFileNameAndExtension, selectFileCerts } from 'tessa/ui/cards/controls';
import { CardGetFileContentRequest, CardRequest, CardService } from 'tessa/cards/service';
import { CdRequestTypes } from './CdRequestTypes';
import { ChFileCategories } from 'src/pkScripts/Helpers/ChFileCategories';
import { Card, CardFileState, ICAdESProvider } from 'tessa/cards';
import { showSelectionDialog } from 'tessa/ui/uiHost';
import { CertData, FileContainer, FileSignatureEventType, IFile, IFileVersion } from 'tessa/files';
import { SelectedValue } from 'tessa/views';
import { CdFileTypes } from './CdFileTypes';
import { CdGeneralDocumentationClientHelper } from './Cards/CdGeneralDocumentationClientHelper';
import { PkCommonHelper } from 'src/pkScripts/Helpers/PkCommonHelper';
import { ValidationResult, ValidationResultBuilder, ValidationResultType } from 'tessa/platform/validation';

export interface CustomCertData extends CertData {
    issuerFullName: string;
    companyAdr?: string;
    extendedKeysUsageOids: string[];
    ownerName?: string;
    ownerOrganization?: string;
}

export class ExtractCertInfo {
    public static GetCertOwnerName(certSubjectName) {
        if (certSubjectName.indexOf('SN=')>0 && certSubjectName.indexOf('G=')>0)
            return this.extract(certSubjectName, 'SN=').substr(3) + " " +  this.extract(certSubjectName, 'G=').substr(2);
        else 
            return this.extract(certSubjectName, 'CN=').substr(3)
    }

    public static GetCertOwnerOrganization(certSubjectName) {
        if (certSubjectName.indexOf('O=')>0)
            return this.extract(certSubjectName, 'O=').substr(2);
        else if (certSubjectName.indexOf('OU=')>0)
            return this.extract(certSubjectName, 'OU=').substr(3);
        else    
            return this.extract(certSubjectName, 'CN=').substr(3);
    }

    public static GetCertOwnerOrganizationAddress(certSubjectName) {
        return this.extract(certSubjectName, 'S=').substr(2) + ", " +
            this.extract(certSubjectName, 'L=').substr(2) + ", " +
            this.extractStreet(certSubjectName, 'STREET=').substr(7);
    }

    private static extract(from, what) {
        let certName = "";
        let begin = from.indexOf(what);
        if (begin >= 0) {
            let end = from.indexOf(', ', begin);
            certName = (end < 0) ? from.substr(begin) : from.substr(begin, end - begin);
        }
        return certName;
    }

    private static extractStreet(from, what) {
        let certName = "";
        let begin = from.indexOf(what);
        if (begin >= 0) {
            let end = from.indexOf('=', begin + what.length);
            certName = (end < 0) ? from.substr(begin) : from.substr(begin, end - begin);
            if (certName.length > 3)
                certName = certName.substr(0, certName.length - 3);
        }
        return certName;
    }
}

export class CdEDSHelper {

    /** Получение сертификатов. */
    public static GetCertsAsync(): Promise<CustomCertData[]> {
        return new Promise((resolve, reject) => {
            window.cadesplugin.async_spawn(
                function*(args) {
                    try {
                        const oStore = yield window.cadesplugin.CreateObjectAsync('CAdESCOM.Store');
                        yield oStore.Open(
                            window.cadesplugin.CAPICOM_CURRENT_USER_STORE,
                            window.cadesplugin.CAPICOM_MY_STORE,
                            window.cadesplugin.CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED);

                        const CertificatesObj = yield oStore.Certificates;
                        const Count = yield CertificatesObj.Count;
                        const certs: CustomCertData[] = [];

                        for (let i = 1; i <= Count; i++) {
                            const cert = yield CertificatesObj.Item(i);
                            const subjectName = yield cert.SubjectName;
                            const issuerName = yield cert.IssuerName;
                            const validFrom = yield cert.ValidFromDate;
                            const validTo = yield cert.ValidToDate;
                            const serialNumber = yield cert.SerialNumber;
                            const certificateStr = yield cert.Export(window.cadesplugin.CADESCOM_ENCODE_BASE64);
                            const extendedKeysUsageOids = Array<string>();
                            const extendedKeyUsage = yield cert.ExtendedKeyUsage();
                            const ekus = yield extendedKeyUsage.EKUs;
                            const ekusCount = yield ekus.Count;
                            for (let j = 1; j < ekusCount; j++) {
                                const eku = yield ekus.Item(j);
                                const ekuOid = yield eku.OID;
                                extendedKeysUsageOids.push(ekuOid);
                            }
                            certs.push({
                                subjectName: CdEDSHelper.GetName(subjectName) as string,
                                issuerName: CdEDSHelper.GetName(issuerName) as string,
                                validFrom,
                                validTo,
                                serialNumber,
                                company: CdEDSHelper.GetOwner(subjectName) as string,
                                issuerFullName: issuerName as string,
                                certificateStr,
                                extendedKeysUsageOids,
                                ownerName: ExtractCertInfo.GetCertOwnerName(subjectName),
                                ownerOrganization: ExtractCertInfo.GetCertOwnerOrganization(subjectName),
                                companyAdr: ExtractCertInfo.GetCertOwnerOrganizationAddress(subjectName)
                            });
                        }

                        yield oStore.Close();

                        args[0](certs);
                    } catch (err) {
                        const lastError = window.cadesplugin.getLastError(err);
                        const errorMessage =
                            "Не удалось обратиться к плагину Crypto-pro.\n" +
                            "Пожалуйста, проверьте подключен ли этот плагин у вас в браузере.\n" +
                            "Если плагин подключен, проверьте его настройки (например, включена ли галочка \"Режим инкогнито\", если вы сидите под инкогнито).\n" +
                            "Если плагина нет, обратитесь к администратору!\n" +
                            "Message: " + lastError;
                        args[1](errorMessage);
                    }
                },
                resolve,
                reject
            );
        });
    }

    private static GetName(str: string | undefined) {
        return this.GetParameter(str, 'CN');
    }

    private static GetOwner(str: string | undefined) {
        return this.GetParameter(str, 'O');
    }

    private static GetParameter(str: string | undefined, marker: string) {
        if (!str) {
            return undefined;
        }

        const strArr = str.split(/=|, /);
        if (strArr.length === 1) {
            return undefined;
        }

        const snIndex = strArr.indexOf(marker);
        if (snIndex === -1 || snIndex === strArr.length) {
            return undefined;
        }

        return strArr[snIndex + 1];
    }

    /** Получить сертификат с комментарием. */
    public static async GetCertificateAsync (
        _edsProvider: ICAdESProvider,
        issuerName: string | null = null,
        enhancedKey: string | null = null,
        _withComment: boolean = false) {
        // получаем список сертификатов
        const fcollection = await this.GetCertsAsync();

        // если было передано наименование организации
        if (issuerName != null) {
            // прлбегаемся по списку сертов
            for (let i = fcollection.length - 1; i >= 0; i--) {
                // получаем серт
                const certificate = fcollection[i];

                // если наименование не содержится в серте
                if (certificate.issuerFullName == null ||
                   !certificate.issuerFullName.includes(issuerName)) {
                    // удаляем серт из списка
                    fcollection.splice(i, 1);
                }
            }
        }

        // если был передан EnhancedKey
        if (enhancedKey != null) {
            // пробегаемся по списку сертов
            for (let i = fcollection.length - 1; i >= 0; i--) {
                // получаем серт
                const certificate = fcollection[i];

                // если ключ не содержится в серте
                if (!this.HasEnhancedKey(certificate, enhancedKey)) {
                    // удаляем серт из списка
                    fcollection.splice(i, 1);
                }
            }
        }

        // если после фильтра не осталось сертификатов
        if (fcollection.length === 0) {
            // возвращаем NULL
            return { Certificate: null, Comment: null };
        }

        // если после фильтра остался 1 сертификат
        if (fcollection.length === 1) {
            // возвращаем его
            return { Certificate: fcollection[0], Comment: null };
        }

        // иначе даем пользователю выбрать сертификат
        const selectedCert = await this.SelectCertificateAsync(fcollection);

        // если выбран 1 сертификат
        if (selectedCert != null) {
            // возвращаем его
            return { Certificate: selectedCert, Comment: null };
        }

        // иначе возвращаем NULL
        return { Certificate: null, Comment: null };
    }

    /** Выбор сертификата из коллекции. */
    private static async SelectCertificateAsync(certDatas: CustomCertData[]) {
        // формируем список значений для выбора
        const values = certDatas.map(certData => {
            const validFrom = new Date(certData.validFrom);
            const validFromDay = validFrom.getDate();
            const validFromMonth = validFrom.getMonth() + 1;
            const validFromYear = validFrom.getFullYear();
            const validTo = new Date(certData.validTo);
            const validToDay = validTo.getDate();
            const validToMonth = validTo.getMonth() + 1;
            const validToYear = validTo.getFullYear();

            const value = new SelectedValue(
                certData.subjectName + ". " +
                "Издатель: " + certData.issuerName + ". " +
                "Действителен с: " +
                (validFromDay < 10 ? "0" : "") + validFromDay + "." +
                (validFromMonth < 10 ? "0" : "") + validFromMonth + "." +
                validFromYear +
                 " по " +
                (validToDay < 10 ? "0" : "") + validToDay + "." +
                (validToMonth < 10 ? "0" : "") + validToMonth + "." +
                validToYear,
                certData);

            return value;
        });

        // отображаем диалоговое окно и получаем выбранное значение
        const selectedValue = await showSelectionDialog(values);

        // возвращаем выбранное значение
        return selectedValue?.value as CustomCertData | null;
    }

    /** Получить сертификат с платформенным окном выбора. */
    public static async GetCertificatePlatformAsync(_edsProvider: ICAdESProvider) {
        const certResult = await selectFileCerts(_edsProvider);
        if (certResult.cancel || !certResult.cert) {
            // иначе возвращаем NULL
            return { Certificate: null, Comment: null };
        }

        let cert = certResult.cert;
        const certs = await this.GetCertsAsync();
        let selectedCert = certs.find(x => x.serialNumber.toLowerCase() == cert.serialNumber.toLowerCase());
        if (selectedCert) {
            return { Certificate: selectedCert, Comment: certResult.comment };
        }

        // иначе возвращаем NULL
        return { Certificate: null, Comment: null };
    }

    /** Проверка наличия Enhanced-ключа в сертификате. */
    private static HasEnhancedKey(certificate: CustomCertData, enhancedKey: string) {
        // возвращаем признак наличия у сертификата указанного ключа
        return certificate.extendedKeysUsageOids.some(x =>
            x === enhancedKey);
    }

    /** Подписать файлы по ID или хешу. */
    public static async SignFilesAsync(
        fileContainer: FileContainer,
        edsProvider: ICAdESProvider,
        card: Card,
        files: {ID: string, Hash?: string | null}[],
        isDetached: boolean = true,
        withoutEnchance: boolean = false,
        certificate: CustomCertData | null = null): Promise<ValidationResultBuilder> {
        let validationResult: ValidationResultBuilder = new ValidationResultBuilder();

        // если не задан сертификат, то даем сотруднику выбрать его
        if (!certificate) {
            try {
                let selectedCert = await this.GetCertificatePlatformAsync(edsProvider);
                if (!selectedCert.Certificate) {
                    validationResult.add(ValidationResultType.Error, "Не выбран сертификат.");
                    return validationResult;
                }
                certificate = selectedCert.Certificate;
            } catch (e) {
                validationResult.add(ValidationResultType.Error, "Не удалось получить сертификат.\n\n" + ValidationResult.fromError(e).toString());
                return validationResult;
            }
        }

        for (let i = 0; i < files.length; i++) {
            let splashMessage: string | undefined =
                files.length == 1
                ? undefined
                : 'Подписание файлов: ' + (i + 1) + " из " + files.length;

            try {
                await this.SetSignatureAsync(
                    fileContainer,
                    card,
                    files[i],
                    isDetached,
                    withoutEnchance,
                    certificate,
                    splashMessage
                );
            } catch (e) {
                validationResult.add(ValidationResultType.Error, "Не удалось подписать файлы.\n\n" + ValidationResult.fromError(e).toString());
                return validationResult;
            }
        }

        return validationResult;
    }
    
    /** Сформировать подпись. */
    private static async SetSignatureAsync(
        fileContainer: FileContainer,
        card: Card,
        unsignedFile: {ID: string, Hash?: string | null},
        isDetached: boolean = true,
        withoutEnchance: boolean = false,
        certificate: CustomCertData | null = null,
        splashMessage: string | undefined = undefined) {

        // ищем файл в контейнере
        var file = fileContainer.files.find(x => x.id == unsignedFile.ID);
        if (!file) {
            throw new Error('Не удалось найти файл для подписания: ' + unsignedFile.ID);
        }
        let versionID = file.lastVersion.id;
        let fileName = file.lastVersion.name;

        // задаем сплэш
        if (!splashMessage) {
            splashMessage = 'Подписание файла: ' + fileName;
        }
        await showLoadingOverlay(async () => {
            if (!file || !certificate) {
                return;
            }

            // получение хеша по госту
            if (!unsignedFile.Hash) {
                unsignedFile.Hash = await this.GetFileHashAsync(
                    card.id,
                    unsignedFile.ID,
                    versionID,
                    fileName,
                    isDetached,
                    certificate);
                if (!unsignedFile.Hash) {
                    throw new Error('Не удалось получить хеш.');
                }
            }

            // подписание хеша
            let signedHash = await this.GetFileSignatureAsync(certificate.serialNumber, unsignedFile.Hash);
            if (!signedHash) {
                throw new Error('Не удалось подписать файл простой подписью.');
            }

            // усиление подписи
            let enhanceSignedHash: string;
            if (withoutEnchance) {
                enhanceSignedHash = signedHash;
            } else {
                enhanceSignedHash = await this.GetEnhanceSignatureByHashAsync(
                    card.id,
                    unsignedFile.ID,
                    versionID,
                    fileName,
                    unsignedFile.Hash,
                    signedHash);
                if (!enhanceSignedHash) {
                    throw new Error('Не удалось подписать файл усовершенствованной подписью.');
                }
            }

            // добавление подписи к файлу
            await this.AddSignatureToFileAsync(
                card,
                fileContainer,
                file,
                enhanceSignedHash,
                isDetached,
                certificate);

        }, splashMessage);
    }

    /** Получить хеш файла. */
    private static async GetFileHashAsync(
        cardID: string | null,
        fileID: string | null,
        versionID: string | null,
        fileName: string | null,
        isDetached: boolean,
        certificate: CustomCertData): Promise<string | null> {

        const request = new CardRequest();
        request.requestType = CdRequestTypes.GetSignatureHash;
        request.cardId = cardID;
        request.info["FileID"] = createTypedField(fileID, DotNetType.Guid);
        request.info["VersionID"] = createTypedField(versionID, DotNetType.Guid);
        request.info["FileName"] = createTypedField(fileName, DotNetType.String);
        request.info["isDetached"] = createTypedField(isDetached, DotNetType.Boolean);
        request.info["OwnerName"] = createTypedField(certificate.subjectName, DotNetType.String);
        request.info["SerialNumber"] = createTypedField(certificate.serialNumber, DotNetType.String);
        request.info["ValidFrom"] = createTypedField(certificate.validFrom, DotNetType.DateTime);
        request.info["ValidTo"] = createTypedField(certificate.validTo, DotNetType.DateTime);

        const response = await CardService.instance.request(request);
        await showNotEmpty(response.validationResult.build());
        if (!response.validationResult.isSuccessful) {
            return null;
        }

        if (!response.info!.UnsignedHash ||
            !response.info!.UnsignedHash.$value) {
            return null;
        } else {
            return response.info.UnsignedHash.$value;
        }
    }

    /** Подписать хеш файла. */
    private static GetFileSignatureAsync(
        certificateSerialNumber: string,
        fileHash: string): Promise<string | null> {
            
        return new Promise(function(resolve, reject) {
            window.cadesplugin.async_spawn(function *(args) {
                try {
                    // TODO TIV: нужно выбирать в зависимости от сертификата
                    const CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_256 = window.cadesplugin.CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_256;
                    
                    // Получаем сертификат нужного формата
                    let certificate;
                    let oStore = yield window.cadesplugin.CreateObjectAsync("CAdESCOM.Store");
                    if (!oStore) return null;
                    yield oStore.Open();
                    let certificatesFromStore = yield oStore.Certificates;
                    let certificatesCount = yield certificatesFromStore.Count;
                    for (let i = 1; i <= certificatesCount; i++) {
                        let currentCertificate = yield certificatesFromStore.Item(i);
                        let currentCertificateSerialNumber = yield currentCertificate.SerialNumber;

                        if (currentCertificateSerialNumber === certificateSerialNumber) {
                            certificate = currentCertificate;
                            break;
                        }
                    }
                    yield oStore.Close();

                    // Без сертификата не подписать, возвращаем null
                    if (!certificate) {
                        return null;
                    }

                    // Переменные для подписания
                    let oSigner = yield window.cadesplugin.CreateObjectAsync("CAdESCOM.CPSigner");
                    yield oSigner.propset_Certificate(certificate);
                    yield oSigner.propset_Options(window.cadesplugin.CAPICOM_CERTIFICATE_INCLUDE_WHOLE_CHAIN);

                    let oHashedData = yield window.cadesplugin.CreateObjectAsync("CAdESCOM.HashedData");
                    yield oHashedData.propset_Algorithm(CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_256);
                    yield oHashedData.SetHashValue(fileHash);
        
                    let oSignedData = yield window.cadesplugin.CreateObjectAsync("CAdESCOM.CadesSignedData");
                    yield oSignedData.propset_ContentEncoding(window.cadesplugin.CADESCOM_BASE64_TO_BINARY);

                    let signedHash = yield oSignedData.SignHash(oHashedData, oSigner, window.cadesplugin.CADESCOM_CADES_BES);
                    args[0](signedHash);
                } catch (e) {
                    args[1](e);
                    return null;
                }
            }, resolve, reject);
        });
    }

    /** Получить усиленную подпись. */
    private static async GetEnhanceSignatureByHashAsync(
        cardID: string,
        fileID: string,
        versionID: string,
        fileName: string,
        hash: string,
        signedHash: string) : Promise<string> {

        const cardGetFileContentRequest = new CardGetFileContentRequest();
        cardGetFileContentRequest.fileTypeName = CdFileTypes.EnhanceSignType;
        cardGetFileContentRequest.cardId = cardID;
        cardGetFileContentRequest.fileId = fileID;
        cardGetFileContentRequest.versionRowId = versionID;
        cardGetFileContentRequest.fileName = fileName;
        cardGetFileContentRequest.info["Hash"] = createTypedField(hash, DotNetType.String);
        cardGetFileContentRequest.info["SignedHash"] = createTypedField(signedHash, DotNetType.String);

        const cardGetFileContentResponse = await CardService.instance.getFileContent(cardGetFileContentRequest);

        if (cardGetFileContentResponse.content == null ||
            cardGetFileContentResponse.size === 0) {
            throw new Error('Ошибка при получении усиленной подписи хеша файла.');
        }

        return PkCommonHelper.GetBase64String(cardGetFileContentResponse.content);
    }

    /** Добавить подпись файлу. */
    public static async AddSignatureToFileAsync(
        card: Card,
        fileContainer: FileContainer,
        file: IFile,
        enhanceSignedHash: string,
        isDetached: boolean,
        certificate: CustomCertData) {

        if (isDetached) {
            await this.AddDetachedSignatureToFileAsync(
                file.lastVersion,
                certificate,
                enhanceSignedHash);

            await this.AddDetachedSignatureFileAsync(
                fileContainer,
                file.lastVersion.name,
                certificate.ownerName ?? certificate.subjectName, 
                enhanceSignedHash);
        } else {
            // файл будет заменен на сервере, чтобы не прокидывать его лишний раз
            let fileCard = card.files.find(x => x.rowId == file.id);
            if (fileCard) {
                fileCard.state = CardFileState.Modified;
                fileCard.info["WithAttachedSignature"] = createTypedField(enhanceSignedHash, DotNetType.String);
            }
        }
    }

    /** Добавление открепленной подписи файлу. */
    private static async AddDetachedSignatureToFileAsync(
        fileVersion: IFileVersion,
        certificate: CustomCertData,
        signatureData: string) {
            
        // создаем токен подписи
        const signatureToken = fileVersion.source.getSignatureCreationToken();
        signatureToken.eventType = FileSignatureEventType.Signed;
        signatureToken.company = certificate.company;
        signatureToken.subjectName = certificate.ownerName ?? certificate.subjectName;
        signatureToken.serialNumber = certificate.serialNumber;
        signatureToken.issuerName = certificate.issuerName;
        signatureToken.data = signatureData;

        // создаем подпись
        const signature = fileVersion.source.createSignature(signatureToken, fileVersion);

        // добавляем подпись в файл
        fileVersion.signatures.push(signature);

        // уведомляем о добавлении подписи
        await fileVersion.source.notifySignAdded(signature);
    }

    /** Добавление файла открепленной подписи в отдельную категорию. */
    public static async AddDetachedSignatureFileAsync(
        fileContainer: FileContainer,
        fileName: string,
        signerName: string,
        signatureContent: string) {
        // генерируем название файла подписи
        const fileSignatureName =
            getFileNameAndExtension(fileName).name +
            "_" +
            signerName +
            ".sgn";

        // формируем контент файла подписи
        const blobData = CdGeneralDocumentationClientHelper.Base64ToBlob(signatureContent, "application/sgn");
        const fileData = new File([blobData], fileSignatureName);

        // создаем файл
        const fileSignature = fileContainer.createFile(
            fileData,
            CdFileTypes.DefaultFileType,
            ChFileCategories.ElectronicSignatures,
            fileSignatureName);

        // добавляем файл
        await fileContainer.addFile(fileSignature, false, true);
    }

    /** Проверить наличие подписей у файла. */
    public static async CheckSignFile(fileVersionRowID) {
        const request = new CardRequest();
        request.requestType = CdRequestTypes.CdCheckSignFile;
        request.info["FileVersionID"] = createTypedField(fileVersionRowID, DotNetType.Guid);

        const response = await CardService.instance.request(request);
        await showNotEmpty(response.validationResult.build());
        if (!response.validationResult.isSuccessful) {
            return null;
        }
    }
}